import { memo, useMemo, ReactNode } from 'react';
import { Button, Popper, useTheme, Typography, styled, ClickAwayListener } from '@mui/material';
import { TreeItem, SimpleTreeView } from '@mui/x-tree-view';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import PopupState, { bindToggle, bindPopper } from 'material-ui-popup-state';

export type TreeNode = {
    id: string;
    name: NonNullable<ReactNode>;
    children?: TreeNode[];
    onClick?: () => void;
};

const LeafIcon = styled(FiberManualRecordIcon)(() => ({
    fontSize: '8px !important',
}));

const renderTree = (nodes: TreeNode[], parentId = '') => {
    return nodes.map((node) => {
        const uniqueId = parentId ? `${parentId}-${node.id}` : node.id;

        return (
            <TreeItem
                key={uniqueId}
                itemId={uniqueId}
                label={node.name}
                onClick={node.onClick}
                slots={{ icon: LeafIcon }}
                sx={{ pt: 0.5, pb: 0.5 }}
            >
                {Array.isArray(node.children) ? renderTree(node.children, uniqueId) : null}
            </TreeItem>
        );
    });
};

export const TreeView = memo((props: { tree: TreeNode[]; label: string; onOpen?: () => void }) => {
    const theme = useTheme();

    const treeNode = useMemo(() => renderTree(props.tree), [props.tree]);

    return (
        <PopupState variant="popper" popupId="tree-view-popup-popper">
            {(popupState) => (
                <>
                    <Button
                        variant="text"
                        {...bindToggle(popupState)}
                        sx={{ color: 'text.primary' }}
                        fullWidth
                        disabled={treeNode.length === 0}
                        endIcon={
                            treeNode.length > 0 && (
                                <KeyboardArrowDownIcon
                                    sx={{
                                        transform: popupState.isOpen ? 'rotate(-180deg)' : 'rotate(0g)',
                                    }}
                                />
                            )
                        }
                        onClick={(event) => {
                            bindToggle(popupState).onClick(event);
                            if (!popupState.isOpen) {
                                props.onOpen?.();
                            }
                        }}
                    >
                        <Typography
                            variant="subtitle2"
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {props.label}
                        </Typography>
                    </Button>
                    {treeNode.length > 0 && (
                        <Popper
                            {...bindPopper(popupState)}
                            placement="left"
                            sx={{
                                minWidth: theme.spacing(25),
                                maxWidth: theme.spacing(50),
                                maxHeight: theme.spacing(50),
                                backgroundColor: theme.palette.background.paper,
                                border: '1px solid',
                                borderColor: theme.palette.divider,
                                overflowY: 'auto',
                                p: 2,
                                zIndex: 2,
                            }}
                            modifiers={[
                                {
                                    name: 'offset',
                                    options: {
                                        offset: [0, -50],
                                    },
                                },
                                {
                                    name: 'flip',
                                    options: {
                                        fallbackPlacements: ['top'],
                                    },
                                },
                            ]}
                        >
                            <ClickAwayListener onClickAway={() => popupState.close()}>
                                <SimpleTreeView
                                    slots={{ expandIcon: ChevronRightIcon, collapseIcon: ExpandMoreIcon }}
                                    sx={{
                                        width: '100%',
                                    }}
                                >
                                    {treeNode}
                                </SimpleTreeView>
                            </ClickAwayListener>
                        </Popper>
                    )}
                </>
            )}
        </PopupState>
    );
});

TreeView.displayName = 'TreeView';
